'use client';

import { Link, Stack, Typography } from '@mui/material';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import 'swiper/css';
import 'swiper/css/effect-fade';
import { Autoplay, EffectFade } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { HighLightHorizontal } from '@/assets/icons';
import { backgrounds, dividers } from '@/assets/materials';
import HexagonalButton from '@/components/bases/HexagonalButton';
import { BUILD_WITH_US_URL, ORBIT_CALENDAR_URL, ORBIT_URL } from '@/global/configs/common';
import { useGA } from '@/hooks/useGA';

const heroSlides = [
  {
    title: 'REINVENTING ADVISORY',
    subtitle:
      'We take products from concept to production, we build products that work fast, clear, and ready for market.',
    background: backgrounds.hero_bg_2,
    primaryButton: {
      label: 'LEARN MORE',
      href: '/about-us',
      target: '_self',
    },
    secondaryButton: {
      label: 'DISCOVER OUR SERVICES',
      href: '/services',
      target: '_self',
    },
  },
  {
    title: 'LFG FOUNDRY',
    subtitle: 'We are a full-service Foundry for companies looking to launch their projects to the next level.',
    background: backgrounds.hero_bg_1,
    primaryButton: {
      label: 'LEARN MORE',
      href: '/about-us',
      target: '_self',
    },
    secondaryButton: {
      label: 'CHECK OUR PRODUCTS',
      href: '/products',
      target: '_self',
    },
  },

  {
    title: 'LFG ORBIT',
    subtitle:
      'Orbit makes global hiring simple - handling payroll, compliance, legal, and labor requirements so you can hire anywhere, effortlessly.',
    background: backgrounds.hero_bg_4,
    primaryButton: {
      label: 'LEARN MORE',
      href: ORBIT_URL,
      target: '_blank',
    },
    secondaryButton: {
      label: 'CONTACT US',
      href: ORBIT_CALENDAR_URL,
      target: '_blank',
    },
  },
  {
    title: 'LFG FORGE',
    subtitle:
      'We provide strategic development services for startups, growing businesses, and enterprise teams, turning ideas into scalable, production-ready systems.',
    background: backgrounds.hero_bg_5,
    primaryButton: {
      label: 'LEARN MORE',
      href: '/services/development-services',
      target: '_self',
    },
    secondaryButton: {
      label: 'CONTACT US',
      href: '/contact-us',
      target: '_self',
    },
  },
  {
    title: 'LFG CORE SALE',
    subtitle: 'A seamless platform to deploy, manage, fundraise, and monetize decentralized networks.',
    background: backgrounds.hero_bg_3,
    primaryButton: {
      label: 'BUILD WITH US',
      href: BUILD_WITH_US_URL,
      target: '_blank',
    },
    secondaryButton: {
      label: 'CONTACT US',
      href: '/contact-us',
      target: '_self',
    },
  },
] as const;

export const HeroSection = () => {
  const [isClient, setIsClient] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const swiperRef = useRef<any>(null);
  const { trackButtonClickEvent } = useGA();

  useEffect(() => {
    setIsClient(true);
  }, []);

  return (
    <Stack
      width={'100%'}
      height={{ xs: '85dvh', sm: '95dvh' }}
      flexGrow={1}
      paddingX={{ xs: '24px', sm: '80px' }}
      paddingY={{ xs: '220px', sm: '176px' }}
      alignItems={'center'}
      justifyContent={'center'}
      component={'section'}
      position={'relative'}>
      {isClient && (
        <Swiper
          onSwiper={swiper => {
            swiperRef.current = swiper;
          }}
          modules={[Autoplay, EffectFade]}
          loop={true}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          speed={3000}
          onSlideChange={swiper => setActiveSlide(swiper.realIndex)}
          style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}>
          {heroSlides.map((slide, index) => (
            <SwiperSlide key={index}>
              <Stack
                width={'100%'}
                height={'100%'}
                sx={{
                  backgroundImage: `url(${slide.background})`,
                  backgroundSize: 'cover',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                }}>
                <Stack
                  zIndex={1}
                  position={'absolute'}
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  sx={{
                    background:
                      'linear-gradient(0deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), linear-gradient(180deg, rgba(6, 9, 18, 0) 50%, #060912 100%)',
                  }}
                />
                <Stack
                  width={{ xs: '320px', sm: 'min(1280px, 100%)' }}
                  zIndex={2}
                  height={'100%'}
                  margin={'0 auto'}
                  paddingX={{ xs: '24px', sm: '80px' }}
                  paddingY={{ xs: '220px', sm: '176px' }}>
                  <Stack gap={{ xs: 4, sm: 7 }} width={'100%'} maxWidth={'807px'}>
                    <Stack width={'100%'} alignItems={'center'} direction={'row'} gap={2}>
                      <HighLightHorizontal width={'65px'} height={'10px'} />
                      <Stack className={'w-full'} justifyContent={'center'}>
                        <Image
                          src={dividers.divider_2}
                          alt={'divider'}
                          width={400}
                          height={2}
                          style={{ width: '100%', height: 'auto' }}
                        />
                      </Stack>
                    </Stack>
                    <Stack gap={4} width={'100%'} paddingRight={{ xs: 0, sm: '47px' }}>
                      <Typography
                        component={'h1'}
                        variant={'displayLarge'}
                        className={'clip-text'}
                        fontSize={{ xs: '30px', sm: '56px' }}
                        lineHeight={{ xs: '40px', sm: '68px' }}
                        fontWeight={700}
                        color={'#A3A3A3'}
                        sx={{
                          background: 'linear-gradient(90deg, #A9A9A9 0%, #FFFFFF 50%, #A9A9A9 100%)',
                        }}>
                        {slide.title}
                      </Typography>
                      <Typography
                        fontSize={{ xs: '14px', sm: '18px' }}
                        component={'h4'}
                        variant={'bodyLarge'}
                        color={'#FFFFFF'}
                        sx={{ opacity: 0.6, whiteSpace: 'pre-line' }}
                        className={'mix-blend'}>
                        {slide.subtitle}
                      </Typography>
                    </Stack>

                    <Stack alignItems={'flex-start'} gap={4} direction={{ xs: 'column', sm: 'row' }} maxWidth={'560px'}>
                      <Link
                        href={slide.primaryButton.href}
                        target={slide.primaryButton.target}
                        style={{ textDecoration: 'none' }}
                        onClick={() => {
                          if (slide.primaryButton.label === 'BUILD WITH US') {
                            trackButtonClickEvent({ name: 'build_with_us' });
                          }
                        }}>
                        <HexagonalButton
                          width={200}
                          variant={'primary'}
                          label={slide.primaryButton.label}
                          labelClassName={'xs:text-[24px]'}
                        />
                      </Link>
                      <Link
                        href={slide.secondaryButton.href}
                        target={slide.secondaryButton.target}
                        style={{ textDecoration: 'none' }}
                        onClick={() => {
                          if (slide.secondaryButton.label === 'CONTACT US') {
                            trackButtonClickEvent({ name: 'contact_us' });
                          }
                        }}>
                        <HexagonalButton
                          width={307}
                          variant={'secondary'}
                          label={slide.secondaryButton.label}
                          labelClassName={'xs:text-[24px]'}
                        />
                      </Link>
                    </Stack>
                    <Stack alignItems={'center'} direction={'row'} gap={2}>
                      <Stack className={'w-full'} justifyContent={'center'}>
                        <Image
                          src={dividers.divider_2}
                          alt={'divider'}
                          width={400}
                          height={2}
                          style={{ width: '100%', height: 'auto', transform: 'rotate(180deg)' }}
                        />
                      </Stack>
                      <HighLightHorizontal width={'65px'} height={'10px'} color={'#FF5319'} />
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      {/* Separate the indicators and navigation buttons */}
      <Stack
        position={'absolute'}
        bottom={'6px'}
        left={'50%'}
        sx={{
          transform: 'translate(-50%, -50%)',
          zIndex: 10,
        }}>
        <Stack width={{ xs: '129px', sm: '384px' }} height={'4px'} direction={'row'} gap={'10px'}>
          {heroSlides.map((_, index) => (
            <Stack
              key={index}
              width={{ xs: '43px', sm: '128px' }}
              height={'4px'}
              onClick={() => {
                if (swiperRef.current) {
                  swiperRef.current.slideToLoop(index);
                }
              }}
              sx={{
                backgroundColor: 'white',
                opacity: activeSlide === index ? 1 : 0.5,
                transition: 'opacity 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                  opacity: activeSlide === index ? 1 : 0.7,
                },
              }}
            />
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};
